import React from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Pagination } from '../../../components/ui/Pagination';
import { FileText } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { convertInchesToFeet, formatDate } from '../../../utils/unitUtils';

type DocWithUrl = { fileUrl?: string; fileName?: string; materials?: Array<{ fileUrl?: string }> };

const hasDownloadableDocs = (docs: DocWithUrl[] | undefined): boolean => {
  if (!docs?.length) return false;
  return docs.some((d) => {
    if (d?.fileUrl) return true;
    const mats = (d as { materials?: Array<{ fileUrl?: string }> }).materials;
    return Array.isArray(mats) && mats.some((m) => m?.fileUrl);
  });
};

const getDownloadableCount = (docs: DocWithUrl[] | undefined): number => {
  if (!docs?.length) return 0;
  return docs.filter((d) => {
    if (d?.fileUrl) return true;
    const mats = (d as { materials?: Array<{ fileUrl?: string }> }).materials;
    return Array.isArray(mats) && mats.some((m) => m?.fileUrl);
  }).length;
};

export type MaterialCategory = 'Pipe' | 'Forge' | 'Gas' | 'Wire Lot' | 'Coating';

interface PipeDashboardTableProps {
  data: Record<string, unknown>[];
  totalItems: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onDocumentClick?: (row: Record<string, unknown>) => void;
  pipeFitterOnly?: boolean;
  category?: MaterialCategory;
}

const renderDocumentsCell = (
  docs: DocWithUrl[] | undefined,
  row: Record<string, unknown>,
  onDocumentClick?: (r: Record<string, unknown>) => void
) => {
  if (!hasDownloadableDocs(docs)) return <TableCell className="text-center w-[80px]">—</TableCell>;
  return (
    <TableCell className="text-center w-[80px]">
      <Button
        className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 mx-auto"
        title="View Documents"
        variant="outline"
        onClick={() => onDocumentClick?.(row)}
      >
        <FileText className="h-4 w-4" />
        {getDownloadableCount(docs)}
      </Button>
    </TableCell>
  );
};

const renderStatusCell = (status: string | undefined) => (
  <TableCell className="text-center">
    <span
      className={cn(
        'px-2 py-1 text-xs rounded-full',
        status === 'Available' && 'bg-green-100 text-green-800',
        status === 'Consumed' && 'bg-gray-100 text-gray-800'
      )}
    >
      {status ?? '-'}
    </span>
  </TableCell>
);

export const PipeDashboardTable: React.FC<PipeDashboardTableProps> = ({
  data,
  totalItems,
  currentPage,
  pageSize,
  loading,
  onPageChange,
  onPageSizeChange,
  onDocumentClick,
  pipeFitterOnly = false,
  category = 'Pipe',
}) => {
  const renderRow = (row: Record<string, unknown>, idx: number) => {
    const details = row.details as Record<string, unknown> | undefined;
    const status = row.materialStatus as string | undefined;
    const date = row.materialDate as string | undefined;
    const docs = row.documents as DocWithUrl[] | undefined;

    if (category === 'Pipe') {
      return (
        <TableRow key={`row-${idx}-${row.materialId ?? ''}`}>
          <TableCell>{details?.pipeNRI != null ? String(details.pipeNRI) : '-'}</TableCell>
          <TableCell>{details?.pipeNo != null ? String(details.pipeNo) : '-'}</TableCell>
          <TableCell>{details?.pipePipeId != null ? String(details.pipePipeId) : '-'}</TableCell>
          <TableCell>{details?.pipeHeatNumber != null ? String(details.pipeHeatNumber) : '-'}</TableCell>
          <TableCell className="text-center">
            {details?.pipeLength != null && typeof details.pipeLength === 'number'
              ? convertInchesToFeet(details.pipeLength as number)
              : '-'}
          </TableCell>
          <TableCell className="text-center">
            {details?.pipeProjectStage != null ? String(details.pipeProjectStage) : '-'}
          </TableCell>
          {renderStatusCell(status)}
          <TableCell className="text-center w-[150px]">{date ? formatDate(date) : '-'}</TableCell>
          {pipeFitterOnly && <TableCell className="text-center">—</TableCell>}
          {renderDocumentsCell(docs, row, onDocumentClick)}
        </TableRow>
      );
    }

    if (category === 'Forge') {
      return (
        <TableRow key={`row-${idx}-${row.materialId ?? ''}`}>
          <TableCell>{details?.forgeNRI != null ? String(details.forgeNRI) : '-'}</TableCell>
          <TableCell>{details?.forgeWorkOrderNumber != null ? String(details.forgeWorkOrderNumber) : '-'}</TableCell>
          <TableCell>{details?.forgeSerialNumber != null ? String(details.forgeSerialNumber) : '-'}</TableCell>
          <TableCell>{details?.forgeHeatNumber != null ? String(details.forgeHeatNumber) : '-'}</TableCell>
          <TableCell className="text-center">
            {details?.forgeProjectStage != null ? String(details.forgeProjectStage) : '-'}
          </TableCell>
          {renderStatusCell(status)}
          <TableCell className="text-center w-[150px]">{date ? formatDate(date) : '-'}</TableCell>
          {renderDocumentsCell(docs, row, onDocumentClick)}
        </TableRow>
      );
    }

    if (category === 'Gas') {
      const extra = details?.gasExtraDetails as Record<string, unknown> | undefined;
      return (
        <TableRow key={`row-${idx}-${row.materialId ?? ''}`}>
          <TableCell>{details?.gasType != null ? String(details.gasType) : '-'}</TableCell>
          <TableCell>{extra?.gasSize != null ? String(extra.gasSize) : '-'}</TableCell>
          <TableCell>{details?.gasSerialNumber != null ? String(details.gasSerialNumber) : '-'}</TableCell>
          <TableCell>{details?.gasLotNumber != null ? String(details.gasLotNumber) : '-'}</TableCell>
          <TableCell>{extra?.gasManufacturer != null ? String(extra.gasManufacturer) : '-'}</TableCell>
          <TableCell>{details?.gasBatchNumber != null ? String(details.gasBatchNumber) : '-'}</TableCell>
          <TableCell>{extra?.gasComposition != null ? String(extra.gasComposition) : '-'}</TableCell>
          <TableCell className="text-center w-[150px]">{date ? formatDate(date) : '-'}</TableCell>
          {renderDocumentsCell(docs, row, onDocumentClick)}
        </TableRow>
      );
    }

    if (category === 'Wire Lot') {
      const extra = details?.wireExtraDetails as Record<string, unknown> | undefined;
      return (
        <TableRow key={`row-${idx}-${row.materialId ?? ''}`}>
          <TableCell>{details?.wireType != null ? String(details.wireType) : '-'}</TableCell>
          <TableCell>{details?.wireLotNumber != null ? String(details.wireLotNumber) : '-'}</TableCell>
          <TableCell>{extra?.wireSizeNumber != null ? String(extra.wireSizeNumber) : '-'}</TableCell>
          <TableCell className="text-center">
            {extra?.wireClassification != null ? String(extra.wireClassification) : '-'}
          </TableCell>
          <TableCell>{extra?.wireManufacturer != null ? String(extra.wireManufacturer) : '-'}</TableCell>
          <TableCell>{details?.wireBatchNumber != null ? String(details.wireBatchNumber) : '-'}</TableCell>
          <TableCell className="text-center w-[150px]">{date ? formatDate(date) : '-'}</TableCell>
          {renderDocumentsCell(docs, row, onDocumentClick)}
        </TableRow>
      );
    }

    if (category === 'Coating') {
      return (
        <TableRow key={`row-${idx}-${row.materialId ?? ''}`}>
          <TableCell>{details?.coatingLotNumber != null ? String(details.coatingLotNumber) : '-'}</TableCell>
          <TableCell>
            {(details?.coatingExtraDetails as Record<string, unknown> | undefined)?.coatingManufacturer != null
              ? String((details?.coatingExtraDetails as Record<string, unknown>)?.coatingManufacturer)
              : '-'}
          </TableCell>
          <TableCell>{details?.coatingBatchNumber != null ? String(details.coatingBatchNumber) : '-'}</TableCell>
          <TableCell className="text-center">
            {Array.isArray(row.assemblyNumbers) ? (row.assemblyNumbers as string[]).join(', ') || '-' : '-'}
          </TableCell>
          <TableCell className="text-center w-[150px]">{date ? formatDate(date) : '-'}</TableCell>
          {renderDocumentsCell(docs, row, onDocumentClick)}
        </TableRow>
      );
    }

    return null;
  };

  const getHeaders = () => {
    if (category === 'Pipe') {
      return (
        <>
          <TableHead>NRI</TableHead>
          <TableHead>Pipe No</TableHead>
          <TableHead>Pipe Id</TableHead>
          <TableHead>Heat Number</TableHead>
          <TableHead className="text-center">Length (ft)</TableHead>
          <TableHead className="text-center">Project Stage</TableHead>
          <TableHead className="text-center">Status</TableHead>
          <TableHead className="text-center w-[100px]">Date</TableHead>
          {pipeFitterOnly && <TableHead className="text-center">Pipe Data</TableHead>}
          <TableHead className="text-center">Documents</TableHead>
        </>
      );
    }
    if (category === 'Forge') {
      return (
        <>
          <TableHead>NRI</TableHead>
          <TableHead>Order Number</TableHead>
          <TableHead>Serial Number</TableHead>
          <TableHead>Heat Number</TableHead>
          <TableHead className="text-center">Project Stage</TableHead>
          <TableHead className="text-center">Status</TableHead>
          <TableHead className="text-center w-[100px]">Date</TableHead>
          <TableHead className="text-center">Documents</TableHead>
        </>
      );
    }
    if (category === 'Gas') {
      return (
        <>
          <TableHead>Consumable Type</TableHead>
          <TableHead>Size</TableHead>
          <TableHead>Serial Number</TableHead>
          <TableHead>Lot Number</TableHead>
          <TableHead>Manufacturer</TableHead>
          <TableHead>Batch Number</TableHead>
          <TableHead>Composition</TableHead>
          <TableHead className="text-center w-[100px]">Date</TableHead>
          <TableHead className="text-center">Documents</TableHead>
        </>
      );
    }
    if (category === 'Wire Lot') {
      return (
        <>
          <TableHead>Consumable Type</TableHead>
          <TableHead>Lot/Certification Number</TableHead>
          <TableHead>Size</TableHead>
          <TableHead className="text-center">Classification</TableHead>
          <TableHead>Manufacturer</TableHead>
          <TableHead>Batch Number</TableHead>
          <TableHead className="text-center w-[100px]">Date</TableHead>
          <TableHead className="text-center">Documents</TableHead>
        </>
      );
    }
    if (category === 'Coating') {
      return (
        <>
          <TableHead>Lot Number</TableHead>
          <TableHead>Manufacturer</TableHead>
          <TableHead>Batch Number</TableHead>
          <TableHead className="text-center">Assembly Number</TableHead>
          <TableHead className="text-center w-[100px]">Date</TableHead>
          <TableHead className="text-center">Documents</TableHead>
        </>
      );
    }
    return null;
  };

  const colSpan = category === 'Pipe' ? (pipeFitterOnly ? 10 : 9) : category === 'Forge' ? 8 : category === 'Gas' ? 9 : category === 'Wire Lot' ? 8 : 6;

  return (
    <Card className="border border-[#E2E5EA] bg-white">
      <CardContent className="flex min-h-[calc(100vh-360px)] flex-col p-4">
        <div className="flex-1 min-h-0 overflow-x-auto">
          <div className="h-full overflow-y-auto">
            <Table className="min-w-[1100px]">
              <TableHeader className="sticky top-0 z-20 bg-white">
                <TableRow>{getHeaders()}</TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} className="text-center py-12">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e04530]" />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : data.length > 0 ? (
                  data.map((row, idx) => renderRow(row, idx))
                ) : (
                  <TableRow>
                    <TableCell colSpan={colSpan} className="text-center text-heritage-grey py-8">
                      No materials found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={onPageChange}
          onItemsPerPageChange={onPageSizeChange}
          pageSizeOptions={[10, 20, 25, 50, 100]}
          showPageNumbers={true}
          maxVisiblePages={5}
        />
      </CardContent>
    </Card>
  );
};
